// utils/useScrollNav.js
import { useEffect, useRef, useState } from "react";

export default function useScrollNav() {
  const navRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  // Hysteresis thresholds (px)
  // Enter "scrolled" when passing ENTER_SHRINK, exit when back above EXIT_SHRINK
  const ENTER_SHRINK = 80; // shrink starts after this
  const EXIT_SHRINK  = 40; // expand only when above this (closer to top)

  // Hide-on-scroll thresholds
  const HIDE_AFTER = 140;  // start allowing hide once past this
  const SHOW_EDGE  = 40;   // never hide near top

  const lastYRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;

      if (!tickingRef.current) {
        window.requestAnimationFrame(() => {
          const lastY = lastYRef.current;
          const goingDown = y > lastY;
          lastYRef.current = y;

          // --- Hysteresis for shrink/expand ---
          setIsScrolled(prev => {
            if (!prev && y >= ENTER_SHRINK) return true;  // enter scrolled
            if (prev && y <= EXIT_SHRINK)  return false;  // exit scrolled
            return prev; // no change to avoid thrash
          });

          // --- Hide-on-scroll (independent from shrink) ---
          // Only allow hiding when sufficiently down the page
          if (y <= SHOW_EDGE) {
            setIsHidden(false);
          } else if (y > HIDE_AFTER) {
            setIsHidden(goingDown);
          }

          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };

    // Initialize once on mount
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { navRef, isScrolled, isHidden };
}
